import { useState } from 'react'
import { Header } from '@/components/DashboardComponents'
import { Button, Textbox, Spacing, Checkbox, TextboxList, RoundIconButton, Dropdown, DropdownOption, RadioGroup, RadioOption } from '@/form/FormComponents'
import { useNavigate, useRouteLoaderData, Form, useSubmit, useRevalidator } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal, ModalListItem } from '@/components/Modal'
import { FormProvider, FormSubmitProvider, FormDataProvider, FormDisabled } from '@/form/FormProvider'
import { joinCourse, unenrollCourse, deleteCourse } from '@/data/CoursesData'
import { useMenu } from '@/components/Menu'
import { useUser } from '@/systems/Authentication'
import { publicAction } from '@/data/loaders'
import { useListThenJoinDepartmentModal } from '@/pages/SettingsLayout'

export function Courses() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const submit = useSubmit();
  const courses = useRouteLoaderData("dashboard_root").data.value.filter(c => !c.archived);
  const { userdata, user } = useUser();

  const [openErrorModal] = useErrorModal();

  const [create_course_loading, setCreateCourseLoading] = useState(false);

  const handleCreateCourse = function() {
    setCreateCourseLoading(true);
    publicAction("getCreateCourseDepartments", {}).then(function({ data }) {
      setCreateCourseLoading(false);

      let departments = data.value;

      const root_org_choices = departments.map(d => d.root_organization_id).reduce((acc, id) => {
        if (!acc.includes(id)) {
          acc.push(id);
        }
        return acc;
      }, []);

      handleCreateCourseChooseOrganization({ root_org_choices, departments });
      
    }).catch(function(error) {
      console.error(error);
      setCreateCourseLoading(false);
      openErrorModal();
    });
  }

  const { handleJoinDepartment } = useListThenJoinDepartmentModal();

  const [openNoOrganizationsModal] = useModal(function ({ closeModal, setLoading }) {
    const handleJoinDepartmentClick = function() {
      handleJoinDepartment({ setLoadingJoinDepartment: (loading) => {
        if (loading) {
          setLoading(true);
        } else {
          closeModal();
        }
      } });
    }

    if (!userdata.organization_id) {
      return (
        <Modal title="You need educator access to create courses">
          <ModalContent>
            {`Your account is limited without educator access. Sign up your organization to gain educator access`}
          </ModalContent>
          <ModalButtons>
            <Button text="Got it" onClick={closeModal}/>
          </ModalButtons>
        </Modal>
      )
    }

    return (
      <Modal title="Join a department to create courses">
        <ModalContent>
          {`Your account is limited until you join a department and gain educator access.`}
          {userdata.organizations_pending.length > 0 ? (
            <>
              <Spacing/>
              {`Your request to join ${userdata.organizations_pending[0].data.name} is still pending.`}
            </>
          ) : null}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          {userdata.organizations_pending.length > 0 ? (
            <Button text="Review request" onClick={() => {closeModal(); navigate("/settings/organizations");}}/>
          ) : (
            <Button text="Join a department" className="primary" onClick={handleJoinDepartmentClick}/>
          )}
        </ModalButtons>
      </Modal>
    )
  });

  const handleCreateCourseChooseDepartment = function({ organization_id, departments }) {
    let department_choices = departments.filter(d => d.root_organization_id == organization_id);

    if (department_choices.length == 1) {
      openNewCourseModal({ organization_id: department_choices[0]._id, department_choices });
    } else if (department_choices.length > 10) {
      chooseDepartmentModal({ department_choices });
    } else {
      openNewCourseModal({ department_choices });
    }
  }

  const [chooseOrganizationModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title="Choose an organization">
        <ModalContent>
          {[...context.root_org_choices].map(a => context.departments.find(d => d.root_organization_id == a).root_organization_data).sort((a, b) => a.name.localeCompare(b.name)).map((org) => <ModalListItem key={org._id} icon="domain" text={org.name} onClick={() => {handleCreateCourseChooseDepartment({ organization_id: org._id, departments: context.departments }); closeModal();}}/>)}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const handleCreateCourseChooseOrganization = function({ root_org_choices, departments }) {
    if (root_org_choices.length == 0) {
      // openNoOrganizationsModal();
      openNewCourseModal({ organization_id: "null", department_choices: [] });
    } else if (root_org_choices.length == 1) {
      handleCreateCourseChooseDepartment({ organization_id: root_org_choices[0], departments });
    } else {
      chooseOrganizationModal({ root_org_choices, departments });
    }
  }

  const [chooseDepartmentModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal title="Choose a department">
        <ModalContent>
          {[...context.department_choices].sort((a, b) => a.name.localeCompare(b.name)).map((department) => <ModalListItem key={department._id} icon="domain" text={department.name} onClick={() => {openNewCourseModal({ organization_id: department._id, department_choices: context.department_choices }); closeModal();}}/>)}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
        </ModalButtons>
      </Modal>
    )
  })

  const [openNewCourseModal] = useModal(function ({ context, closeModal }) {
    function submitCreateCourse({ formatted_data }) {
      closeModal();
  
      if (!formatted_data.multiple_sections) {
        formatted_data.sections = [];
      }
      delete formatted_data.multiple_sections;
  
      submit({intent: "createCourse", data: { ...formatted_data }}, { method: "post", encType: "application/json" });
    }

    return (
      <Modal title="Create course">
        <FormProvider onSubmit={submitCreateCourse}>
          <Form id="create_course" method="post" onSubmit={(event) => event.preventDefault()}>
            <FormSubmitProvider>
              {({handleSubmit}) => (
                <>
                  <ModalContent>
                    <Dropdown name="organization_id" title="Department" required="Please select a department" default_value={context.organization_id || undefined}>
                      {(context.department_choices.length == 0 ? [{_id: "null", name: "None"}] : []).concat(context.department_choices).map((department) => <DropdownOption key={department._id} name={department._id} text={department.name}/>)}
                    </Dropdown>
                    <Spacing/>
                    <Textbox name="name" title="Course name" placeholder="Wilderness Medicine" required="Please enter course name" />
                    <Spacing/>
                    <Checkbox text="Multiple sections/periods" name="multiple_sections" default_value={false}/>
                    <Spacing/>
                    <FormDataProvider fields={["multiple_sections"]}>{({data}) => (
                      <FormDisabled obviated={!data.multiple_sections}>
                        <TextboxList name="sections" title="Sections" add_button_label="Add section" start_with_one={true} required="Please add at least one section"/>
                      </FormDisabled>
                    )}</FormDataProvider>
                    <Spacing/>
                    <RadioGroup title="Feedback mode" name="feedback_mode" default_value="review">
                      <RadioOption name="review" text="Educator must review and return"/>
                      <RadioOption name="auto-return" text="Return feedback immediately, without review"/>
                    </RadioGroup>
                  </ModalContent>
                  <ModalButtons>
                    <Button text="Cancel" className="hollow" onClick={closeModal}/>
                    <Button text="Submit" className="primary" onClick={handleSubmit}/>
                  </ModalButtons>
                </>
              )}
            </FormSubmitProvider>
          </Form>
        </FormProvider>
      </Modal>
    )
  });

  const [openJoinCourseModal] = useModal(function ({ closeModal, setLoading }) {
    function submitJoinCourse({ formatted_data, focusError }) {
      setLoading(true);
  
      joinCourse({ join_code: formatted_data.join_code.split("/").reverse()[0] }).then(function(data) {
        closeModal();
        revalidator.revalidate();
        // navigate("/courses/"+data.data.course_id);
      }).catch(function(error) {
        setLoading(false);
        if (error.message == "Failed to join course") {
          focusError({name: "join_code", error: "Invalid course code"});
        } else {
          console.error(error);
          focusError({name: "join_code", error: "Something went wrong, please try again."});
        }
      });
    }

    return (
      <Modal title="Join course">
        <FormProvider onSubmit={submitJoinCourse}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="join_code" title="Course code" onEnter={handleSubmit} required="Please enter a course code" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  return (
    <>
      <Header title="Courses">
        {userdata.is_educator ? <Button icon="plus" text="New course" onClick={handleCreateCourse} loading={create_course_loading}/> : null}
        {user.isAnonymous ? null : <Button icon="plus" text="Join course" onClick={openJoinCourseModal}/>}
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <div className="dashboard_content_container">
          {
            courses.length > 0 ? (
              <div className="course_items">
                {courses.sort((a, b) => a.name.localeCompare(b.name)).map((course) => <CourseItem key={course._id} course={course}/>)}
              </div>
            ) : <GenericEmptyListPlaceholder text={userdata.is_educator ? "You haven't created any courses yet" : "You aren't in any courses yet"} icon="book-open-variant-outline"/>
          }
        </div>
      </div>
    </>
  )
}

export function ArchivedCourses() {
  const courses = useRouteLoaderData("dashboard_root").data.value;
  const archived_courses = courses.filter(c => c.archived == true);

  return (
    <>
      <Header title="Archived courses"/>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <div className="dashboard_content_container">
          {
            archived_courses.length > 0 ? (
              <div className="course_items">
                {archived_courses.sort((a, b) => a.name.localeCompare(b.name)).map((course) => <CourseItem key={course._id} course={course}/>)}
              </div>
            ) : <GenericEmptyListPlaceholder text="No archived courses" icon="archive"/>
          }
        </div>
      </div>
    </>
  )
}

export const useArchiveCourseModal = () => {
  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [openArchiveCourseModal] = useModal(function ({ context, closeModal, setLoading }) {
    const { course_id } = context;
    
    function submitArchiveCourse() {
      setLoading(true);
  
      publicAction("archiveCourse", { course_id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Archive course?">
        <ModalContent>
          {"Assessments, learner submissions, feedback, and scoring will not be deleted. Instructors and learners will still be able to view everything."}
          <Spacing/>
          {"You can't undo this."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Archive course" className="destructive" onClick={submitArchiveCourse}/>
        </ModalButtons>
      </Modal>
    )
  });

  return [openArchiveCourseModal];
}

export const useRenameCourseModal = () => {
  const revalidator = useRevalidator();

  const [openRenameCourseModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitRenameCourse({ formatted_data, focusError }) {
      setLoading(true);

      publicAction("renameCourse", {
        course_id: context.course_id,
        name: formatted_data.name
      }).then(function() {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        setLoading(false);
        focusError({name: "name", error: "Something went wrong, please try again."});
      });
    }

    return (
      <Modal title="Rename course">
        <FormProvider onSubmit={submitRenameCourse}>
          <FormSubmitProvider>
            {({handleSubmit}) => (
              <>
                <ModalContent>
                  <Textbox name="name" default_value={context.name} required="Please enter course name" />
                </ModalContent>
                <ModalButtons>
                  <Button text="Cancel" className="hollow" onClick={closeModal}/>
                  <Button text="Submit" className="primary" onClick={handleSubmit}/>
                </ModalButtons>
              </>
            )}
          </FormSubmitProvider>
        </FormProvider>
      </Modal>
    )
  });

  return [openRenameCourseModal];
}

function CourseItem({course}) {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [openUnenrollCourseModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitUnenrollCourse() {
      setLoading(true);
  
      unenrollCourse({ id: context.course_id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Unenroll from course?">
        <ModalContent>
          You will be removed from the course.
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Confirm" className="destructive" onClick={submitUnenrollCourse}/>
        </ModalButtons>
      </Modal>
    )
  });

  const [openArchiveCourseModal] = useArchiveCourseModal();
  const [openRenameCourseModal] = useRenameCourseModal();

  const [openDeleteCourseModal] = useModal(function ({ context, closeModal, setLoading }) {
    function submitDeleteCourse() {
      setLoading(true);
  
      deleteCourse({ course_id: context.course_id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Delete course forever?">
        <ModalContent>
          {`This will permanently delete all assessments, learner submissions, feedback, and scoring for the whole course.`}
          <Spacing/>
          {"You can't undo this."}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Delete course" className="destructive" onClick={submitDeleteCourse}/>
        </ModalButtons>
      </Modal>
    )
  });

  const { handleContextMenu, handleOnClick } = useMenu({
    menuItems: course.membership_type == "educator" ? (
      course.archived !== true ? [
        {
          label: 'Rename',
          icon: 'rename-outline',
          onClick: () => openRenameCourseModal({ course_id: course._id, name: course.name }),
        },
        // {
        //   label: 'Make a copy',
        //   icon: 'copy',
        //   onClick: () => {},
        // },
        "separator",
        {
          label: 'Archive',
          icon: 'archive',
          onClick: () => openArchiveCourseModal({ course_id: course._id }),
        }
      ] : [
        {
          label: 'Delete forever',
          icon: 'delete-forever-outline',
          onClick: () => openDeleteCourseModal({ course_id: course._id }),
        }
      ]
    ) : [
      {
        label: 'Unenroll',
        icon: 'logout',
        onClick: () => openUnenrollCourseModal({ course_id: course._id }),
      }
    ]
  });

  return (
    <div className="course_item" onClick={() => {navigate("/courses/"+course._id)}} onContextMenu={handleContextMenu} tabIndex={0}>
      <div>
        <div>
          <div className="course_name">{course.name}</div>
          <div className="course_section">{course.section_name || ""}</div>
        </div>
        <div>
          <RoundIconButton icon="dots-vertical" className="small" onClick={handleOnClick} />
        </div>
      </div>
    </div>
  )
}